import React, { useState } from "react";
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Snackbar,
    Alert,
    Stack,
} from "@mui/material";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import axios from "axios";
import { API_URL } from "../config";
import { SectionHeading } from "../utils";

import {useNavigate} from 'react-router-dom';
const RECORD_NAME_MAX_LENGTH = 25;

const UploadMenu = () => {
    const navigate=useNavigate();
    const [restaurantName, setRestaurantName] = useState("");
    const [menuFile, setMenuFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const trimmedRestaurantName = restaurantName.trim();

        if (trimmedRestaurantName.length > RECORD_NAME_MAX_LENGTH) {
            setErrorMessage(`File name must be ${RECORD_NAME_MAX_LENGTH} characters or fewer.`);
            setSuccessMessage("");
            setLoading(false);
            return;
        }

        const formData = new FormData();
        formData.append("restaurant_name", trimmedRestaurantName);
        formData.append("menu_file", menuFile);

        try {
            const response = await axios.post(
                API_URL + "upload-doc.php",
                formData,
                {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true,
                }
            );

            if (response.data.success) {
                setSuccessMessage(response.data.message);
                navigate('/view-qr/'+response.data.base36_id);
                setErrorMessage("");
            } else {
                setErrorMessage(response.data.message);
                setSuccessMessage("");
            }
        } catch (error) {
            setErrorMessage(error.response?.data?.message || "The file could not be uploaded. Please retry.");
            setSuccessMessage("");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container maxWidth="md" className="form-shell upload-menu-shell">
            <Box className="form-shell-badge">
                <CloudUploadRoundedIcon fontSize="small" />
                <Typography variant="caption">File uploader</Typography>
            </Box>

            <SectionHeading sx={{ marginTop: "-8px" }}>
                Upload with confidence
            </SectionHeading>

            <Typography variant="h6" gutterBottom className="form-subheading">
                Name your file, attach it, then continue to design and test its QR code.
            </Typography>

            <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} className="form-feature-row">
                <Box className="form-feature-card">
                    <DescriptionRoundedIcon fontSize="small" />
                    <Box>
                        <Typography variant="subtitle2">Supported files</Typography>
                        <Typography variant="body2">PDFs, images, text, CSV, vCards, calendars, MP3 and MP4. Up to 20 MB.</Typography>
                    </Box>
                </Box>
                <Box className="form-feature-card">
                    <AutoAwesomeRoundedIcon fontSize="small" />
                    <Box>
                        <Typography variant="subtitle2">Cleaner handoff</Typography>
                        <Typography variant="body2">Give each upload a clear label for faster management later.</Typography>
                    </Box>
                </Box>
            </Stack>

            <form onSubmit={handleSubmit} className="form-stack">
                <Stack spacing={1.5}>
                    <TextField
                        label="File Name"
                        helperText={`${restaurantName.length}/${RECORD_NAME_MAX_LENGTH} characters`}
                        value={restaurantName}
                        onChange={(e) => setRestaurantName(e.target.value.slice(0, RECORD_NAME_MAX_LENGTH))}
                        fullWidth
                        required
                        variant="outlined"
                        className="form-input"
                        inputProps={{ maxLength: RECORD_NAME_MAX_LENGTH }}
                    />

                    <TextField
                        type="file"
                        helperText="Select the file you want to attach to this record."
                        InputLabelProps={{ shrink: true }}
                        onChange={(e) => setMenuFile(e.target.files[0])}
                        fullWidth
                        required
                        variant="outlined"
                        className="form-input file-input"
                    />
                </Stack>

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    className="form-submit-btn"
                    disabled={loading}
                >
                    {loading ? <CircularProgress size={24} color="inherit" /> : "Upload File"}
                </Button>
            </form>

            {successMessage && (
                <Snackbar
                    open={true}
                    autoHideDuration={4000}
                    onClose={() => setSuccessMessage("")}
                >
                    <Alert severity="success" onClose={() => setSuccessMessage("")}>{successMessage}</Alert>
                </Snackbar>
            )}

            {errorMessage && (
                <Snackbar
                    open={true}
                    autoHideDuration={4000}
                    onClose={() => setErrorMessage("")}
                >
                    <Alert severity="error" onClose={() => setErrorMessage("")}>{errorMessage}</Alert>
                </Snackbar>
            )}
        </Container>
    );
};

export default UploadMenu;
